import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Play, Pause, Volume2, ShoppingBag } from "lucide-react";

// Ensures only one preview plays at a time across all album sections.
let activeStop = null;

const RevealText = ({ text }) => {
  const words = text.split(" ");
  return (
    <p className="font-serif text-xl md:text-2xl leading-[1.5] text-[#d8d5ce] max-w-xl">
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            initial={{ y: "100%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 12) * 0.03 }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </p>
  );
};

const Equalizer = ({ color }) => (
  <div className="flex items-end gap-[3px] h-4" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className="w-[3px] rounded-full"
        style={{
          background: color,
          height: "100%",
          animation: `eq 0.9s ease-in-out ${i * 0.12}s infinite`,
          transformOrigin: "bottom",
        }}
      />
    ))}
  </div>
);

export const AlbumSection = ({ album, index, number, kind }) => {
  const ref = useRef(null);
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const coverScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);
  const glow = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [0, 0.5, 0]);
  const numX = useTransform(scrollYProgress, [0, 1], [40, -40]);

  const flip = index % 2 === 1;
  const hasPreview = Boolean(album.preview);

  const fadeIn = () => {
    const a = audioRef.current;
    if (!a) return;
    if (activeStop) activeStop();
    activeStop = () => {
      clearInterval(fadeRef.current);
      a.pause();
      setPlaying(false);
    };
    clearInterval(fadeRef.current);
    try {
      a.currentTime = 0;
      a.volume = 0;
      a.play().then(() => setPlaying(true)).catch(() => {});
    } catch (e) {}
    fadeRef.current = setInterval(() => {
      if (!a) return;
      a.volume = Math.min(0.85, a.volume + 0.08);
      if (a.volume >= 0.84) clearInterval(fadeRef.current);
    }, 60);
  };

  const fadeOut = () => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    fadeRef.current = setInterval(() => {
      if (!a) return;
      a.volume = Math.max(0, a.volume - 0.12);
      if (a.volume <= 0.02) {
        a.pause();
        setPlaying(false);
        clearInterval(fadeRef.current);
      }
    }, 45);
  };

  const onEnter = () => {
    setHovered(true);
    if (hasPreview) fadeIn();
  };
  const onLeave = () => {
    setHovered(false);
    if (hasPreview) fadeOut();
  };
  const onTap = () => {
    if (!hasPreview) return;
    if (playing) fadeOut();
    else fadeIn();
  };

  useEffect(() => () => {
    clearInterval(fadeRef.current);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  return (
    <section
      ref={ref}
      data-testid={`album-section-${album.id}`}
      className="relative min-h-[100svh] flex items-center px-6 md:px-12 py-24 overflow-hidden"
    >
      {/* scroll accent glow */}
      <motion.div
        style={{ opacity: glow, background: `radial-gradient(60% 60% at ${flip ? "80%" : "20%"} 50%, ${album.accent}, transparent 70%)` }}
        className="absolute inset-0 pointer-events-none"
      />
      {/* hover breathing glow */}
      <motion.div
        animate={{ opacity: hovered ? 0.4 : 0 }}
        transition={{ duration: 0.6 }}
        style={{ background: `radial-gradient(50% 50% at ${flip ? "78%" : "22%"} 50%, ${album.accent}, transparent 68%)` }}
        className="absolute inset-0 pointer-events-none"
      />

      <motion.span
        style={{ x: numX }}
        className={`pointer-events-none absolute ${flip ? "left-2 md:left-8" : "right-2 md:right-8"} top-8 font-machine font-semibold text-[26vw] leading-none text-white/[0.04] select-none`}
      >
        {String(number ?? index + 1).padStart(2, "0")}
      </motion.span>

      <div className={`relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center w-full max-w-[1500px] mx-auto ${flip ? "md:[direction:rtl]" : ""}`}>
        {/* Cover */}
        <div className="md:col-span-6 [direction:ltr]">
          <motion.div style={{ y: coverY }} className="relative">
            <motion.div
              style={{ scale: coverScale }}
              onMouseEnter={onEnter}
              onMouseLeave={onLeave}
              onClick={onTap}
              className="relative aspect-square w-full overflow-hidden cursor-pointer group/cover"
              data-testid={`album-preview-trigger-${album.id}`}
            >
              <motion.img
                src={album.cover}
                alt={`${album.title} artwork`}
                data-testid={`album-cover-${album.id}`}
                className="w-full h-full object-cover"
                animate={playing ? { scale: [1, 1.035, 1] } : { scale: hovered ? 1.02 : 1 }}
                transition={playing ? { duration: 3.6, repeat: Infinity, ease: "easeInOut" } : { duration: 0.6 }}
              />
              <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 120px 20px rgba(0,0,0,0.6)" }} />
              <div className="absolute inset-4 border border-white/15 pointer-events-none" />

              <AnimatePresence>
                {hovered && hasPreview && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  >
                    <motion.div
                      initial={{ scale: 0.7 }}
                      animate={{ scale: 1 }}
                      className="h-16 w-16 rounded-full backdrop-blur-md bg-black/40 border border-white/30 flex items-center justify-center"
                    >
                      {playing ? (
                        <Pause size={20} className="fill-white text-white" />
                      ) : (
                        <Play size={20} className="fill-white text-white translate-x-[1px]" />
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {playing && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-4 bottom-4 flex items-center gap-3 px-4 py-2 backdrop-blur-md bg-black/50 border border-white/15"
                    data-testid={`album-now-playing-${album.id}`}
                  >
                    <Equalizer color={album.accent} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#f2f0ec]">Playing</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {hasPreview && <audio ref={audioRef} src={album.preview} preload="none" loop />}
            </motion.div>

            <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
              <span className="flex items-center gap-2">
                {hasPreview && <Volume2 size={12} style={{ color: album.accent }} />}
                {hasPreview ? (playing ? "Playing" : "Click to play") : (album.format.length > 3 ? album.format : "")}
              </span>
              <span style={{ color: album.accent }}>{album.tag}</span>
            </div>
          </motion.div>
        </div>

        {/* Text */}
        <div className="md:col-span-6 [direction:ltr]">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10" style={{ background: album.accent }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: album.accent }}>
              {kind}
            </span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-light tracking-tight text-[#f2f0ec] text-4xl md:text-6xl leading-[0.92] mb-8"
          >
            {album.title}
          </motion.h2>

          <RevealText text={album.world} />

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to={`/world/${album.id}`}
              data-testid={`album-enter-${album.id}`}
              className="group inline-flex items-center gap-3 border border-white/20 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#f2f0ec] hover:bg-[#f2f0ec] hover:text-black transition-all duration-500"
            >
              Enter World
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>

            {album.stream ? (
              <a
                href={album.stream}
                target="_blank"
                rel="noreferrer"
                data-testid={`album-stream-${album.id}`}
                className="group inline-flex items-center gap-2 border border-white/15 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#d8d5ce] hover:text-[#f2f0ec] hover:border-white/40 transition-all duration-500"
              >
                <Play size={12} className="fill-current" />
                Stream
              </a>
            ) : (
              <span
                data-testid={`album-stream-${album.id}`}
                title="Streaming link coming soon"
                className="inline-flex items-center gap-2 border border-white/10 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#555] cursor-not-allowed select-none"
              >
                <Play size={12} className="fill-current" />
                Stream
              </span>
            )}

            <a
              href={album.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`album-buy-${album.id}`}
              className="group inline-flex items-center gap-2 border border-white/15 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#d8d5ce] hover:text-[#f2f0ec] hover:border-white/40 transition-all duration-500"
            >
              <ShoppingBag size={12} />
              Buy
              <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
